import React, { useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, TextField, Chip,
  Tooltip, Divider, Grid, Badge, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { t } from 'i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { updateManageByIdAction } from '../../../../actions/mangeActionV2';
import { showErrorToast, showSuccessToast } from '../../../../slices/toastSlice';

// Dialog مُحسن لعرض / إكمال البيانات الناقصة للحاويات مع ملخص وتقدم
export default function MissingInfoDialog({ open, onClose, row, onSave }) {
  const firstMissingRef = useRef(null);
  const dispatch = useDispatch();
  const updateManage = useSelector(s => s.manageV2.updateManage) || {};
  const isLoading = updateManage.loading === 'pending';
  const error = updateManage.errorMessage;


  const initialValues = useMemo(() => ({
  
          ...row,
          id: row?.id,
          primaryClient: {id :row?.primaryClientId , name : row?.primaryClientName,company : row?.primaryClientCompany , exporter :  row?.primaryClientExporterId? {id :row?.primaryClientExporterId ,name: row?.primaryClientExporterName}: null} || null,
          clientExporter: row?.exporter || null,
          exporter: row?.exporter ,
          Consignee: {},
          notifyParty: {},
          consigneeDock: {},
          primaryBillOfLadingFile: row?.primaryBillOfLadingFile || [],
          primaryDescription: row?.primaryDescription || row?.description || 'Used Vehicles',
          shipmentMode:  row?.shipmentMode || row?.type || null,
          shipmentClients: (row?.shipmentClients || []).map(cl =>({client: cl})),
          containers: (row?.containers || []).map(container => ({
            id: container.id,
            containerSize: container.containerSize ?? null,
            containerNumber: container.containerNumber ?? null,
            sealNumber: container.sealNumber ?? null,
            netWeight: container.netWeight ?? 0,
            shipmentItems: (container.shipmentItems  || []).map((item) => {              
              const vehicle = {id: item.vehicle.id, vin: item.vehicle?.vin, make: {name :item?.vehicle?.make}, model: {name:item?.vehicle?.model}, year: item?.vehicle?.year,fuelType:{name : item?.vehicle?.fuelType} , vehicleType :{name:item?.vehicle?.vehicleType} , weight:item?.vehicle?.weight || 0 , price:item?.vehicle?.price || 0           
              ,client:{id : item?.vehicle?.clientId, name : item?.vehicle?.clientName, company : item?.vehicle?.clientCompany}
              };
              return {
                id: item.id,
                vehicle,
                consignee: item?.consignee ,
                notifyParty: item?.notifyParty ,
                consigneeDock: item?.consigneeDock ,
                description: item?.description,
                billOfLadingFile: item?.billOfLadingFile || [],
              };
            }),
          }))


  }), [row]);

  const validationSchema = Yup.object({
    containers: Yup.array().of(
      Yup.object({
        containerNumber: Yup.string().trim().required(t('Container number is required')),
        sealNumber: Yup.string().trim().required(t('Seal number is required')),
      })
    )
  });

  const summary = useMemo(() => {
    if (!row) return null;
    const totalContainers = row.containers?.length || 0;
    const missingNumberCount = row.containers?.filter(c => !c.containerNumber).length || 0;
    const missingSealCount = row.containers?.filter(c => !c.sealNumber).length || 0;
    const totalVehicles = row.containers?.reduce((s, c) => s + (c.shipmentItems?.length || 0), 0) || 0;
    const client = {id :row?.primaryClientId , name : row?.primaryClientName,company : row?.primaryClientCompany , exporter :  row?.primaryClientExporterId? {id :row?.primaryClientExporterId ,name: row?.primaryClientExporterName}: null} || row?.client || {};
    const clientCompany = client?.companyName || client?.company || client?.company_name || client?.companyEn || client?.companyAR || '';
    return {
      totalContainers,
      missingNumberCount,
      missingSealCount,
      totalVehicles,
      bookingNumber: row?.booking?.number,
      bookingStatus: row?.booking?.bookingStatus?.name,
      vesselName: row?.booking?.vesselName,
      voyageNumber: row?.booking?.voyageNumber,
      destination: row?.booking?.destination?.name,
      shippingLine: row?.booking?.shippingLine?.name,
      clientName: client?.name,
      clientCompany,
    };
  }, [row]);

  const partyData = useMemo(() => {
    if (!row) return null;
    const exporter = row.exporter || row?.client?.exporter || row?.containers?.find(c => c.exporter)?.exporter || null;
    const consignees = row.consignee ? [row.consignee] : [];
    const notifyParty = row.notifyParty ? [row.notifyParty] : [];
    const consigneeDocks = row.consigneeDock ? [row.consigneeDock] : [];
    return { exporter, consignees, notifyParty, consigneeDocks };
  }, [row]);



 


  const handleSubmit = async (values) => {
    if (!row) return;
    const payload = values
    dispatch(updateManageByIdAction({ mode: 'shipment', id: row.id, data: payload }));
  };

  useEffect(() => {
    if (updateManage.loading === "succeeded" && open) {
      dispatch(showSuccessToast(t('Containers updated successfully')));
      if (onSave && row) {
        onSave();
      }
      onClose();
    }
    if (updateManage.loading === "failed") {
      dispatch(showErrorToast(error || t('Failed to update containers')));
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateManage.loading]);

  // Focus first missing field only when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (firstMissingRef.current && typeof firstMissingRef.current.focus === 'function') {
          firstMissingRef.current.focus();
        }
      }, 160);
    } else {
      firstMissingRef.current = null;
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {(formik) => {
          const containers = formik.values.containers || [];
          const totalFields = containers.length * 2;
          const filledFields = containers.reduce((s, c) => s + (c.containerNumber ? 1 : 0) + (c.sealNumber ? 1 : 0), 0);
          const completion = totalFields ? Math.round((filledFields / totalFields) * 100) : 100;
          const hasMissing = filledFields !== totalFields;

          return (
            <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7 }}>
                <WarningAmberRoundedIcon sx={{ color: hasMissing ? 'warning.main' : 'success.main' }} fontSize="small" />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: .75 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: hasMissing ? 'warning.main' : 'success.main' }}>
                    {t('Missing data')} {row?.id ? `#${row.id}` : ''}
                  </Typography>
                  <LinearProgress variant="determinate" value={completion} sx={{ height: 6, borderRadius: 4, bgcolor: '#ECEFF1', '& .MuiLinearProgress-bar': { borderRadius: 4 } }} />
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ bgcolor: '#F5F7FA', maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
        {summary && (
          <Accordion defaultExpanded disableGutters sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 46, '& .MuiAccordionSummary-content': { my: 0 } }}>
              <Typography sx={{ fontWeight: 600 }}>{t('Shipment Summary')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75, ml: 2 }}>
                <Chip size="small" variant="outlined" label={`${t('Containers')}: ${summary.totalContainers}`} />
                <Chip size="small" color={summary.missingNumberCount ? 'warning' : 'success'} variant="outlined" label={`${t('Number missing')}: ${summary.missingNumberCount}`} />
                <Chip size="small" color={summary.missingSealCount ? 'error' : 'success'} variant="outlined" label={`${t('Seal missing')}: ${summary.missingSealCount}`} />
                <Chip size="small" variant="outlined" label={`${t('Vehicles')}: ${summary.totalVehicles}`} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={1.2} sx={{ p: .5 }}>
                <Grid item xs={6} md={3}><Field label={t('Booking Number')} value={summary.bookingNumber} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Status')} value={summary.bookingStatus} chip color="info" /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Vessel Name')} value={summary.vesselName} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Voyage Number')} value={summary.voyageNumber} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Destination')} value={summary.destination} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Shipping Line')} value={summary.shippingLine} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Client')} value={summary.clientName} /></Grid>
                <Grid item xs={6} md={3}><Field label={t('Company Name')} value={summary.clientCompany} /></Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">{t('Please complete the missing container numbers and seal numbers below.')}</Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {partyData && (
          <Accordion defaultExpanded disableGutters sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 46, '& .MuiAccordionSummary-content': { my: 0 } }}>
              <Typography sx={{ fontWeight: 600 }}>{t('Parties')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={2} sx={{ p: .5 }}>
                {partyData.exporter && (
                  <Grid item xs={12} md={6}>
                    <PartyCard title={t('Exporter')} data={partyData.exporter} />
                  </Grid>
                )}
                {!!partyData.consignees.length && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {partyData.consignees.map(c => (
                        <PartyCard key={c.id || c.name} title={t('Consignee')} data={c} dashed />
                      ))}
                    </Box>
                  </Grid>
                )}




                 {!!partyData.notifyParty.length && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {partyData.notifyParty.map(c => (
                        <PartyCard key={c.id || c.name} title={t('Notify Party')} data={c} dashed />
                      ))}
                    </Box>
                  </Grid>
                )}



                
                 {!!partyData.consigneeDocks.length && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {partyData.consigneeDocks.map(c => (
                        <PartyCard key={c.id || c.name} title={t('Consignee Dock')} data={c} dashed />
                      ))}
                    </Box>
                  </Grid>
                )}


              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion defaultExpanded disableGutters sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 46, '& .MuiAccordionSummary-content': { my: 0, alignItems: 'center' } }}>
            <Typography sx={{ fontWeight: 600 }}>{t('Containers')}</Typography>
            <Chip size="small" sx={{ ml: 1 }} color={hasMissing ? 'warning' : 'success'} label={hasMissing ? t('Incomplete') : t('Complete')} />
            <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={`${filledFields}/${totalFields}`} />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {containers.map((c, idx) => {
                const missingNumber = !c.containerNumber;
                const missingSeal = !c.sealNumber;
                const highlight = missingNumber || missingSeal;
                return (
                  <Box key={c.id || idx} sx={{ p: 1.6, border: '1px solid', borderColor: highlight ? (missingSeal ? 'error.light' : 'warning.light') : '#E2E8F0', bgcolor: highlight ? (missingSeal ? '#FFF5F5' : '#FFF9E6') : '#FFFFFF', borderRadius: 2, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: '230px 1fr 1fr 200px 0.5fr' }, position: 'relative', transition: 'border-color .25s, background .25s', '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.06)' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: .75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                        <Badge color={highlight ? (missingSeal ? 'error' : 'warning') : 'default'} variant={highlight ? 'dot' : undefined}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{t('Container')} #{idx + 1}</Typography>
                        </Badge>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.containerSize || '-'}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                        {missingNumber && <Chip size="small" variant="outlined" color="warning" label={t('Number missing')} />}
                        {missingSeal && <Chip size="small" variant="outlined" color="error" label={t('Seal missing')} />}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                        <Chip size="small" variant="outlined" label={`${t('Vehicles')}: ${(c.vehicles?.length || 0)}`} />
                        <Chip size="small" variant="outlined" label={`${t('Net Weight')}: ${(c.netWeight || 0)}`} />
                      </Box>
                    </Box>
                    <TextField
                      inputRef={ref => { if (ref && !firstMissingRef.current && missingNumber) firstMissingRef.current = ref; }}
                      label={t('Container Number')}
                      value={c.containerNumber}
                      disabled={isLoading}
                      onChange={e => formik.setFieldValue(`containers.${idx}.containerNumber`, e.target.value.toUpperCase())}
                      size="small"
                      error={missingNumber}
                      placeholder={t('Enter number')}
                      helperText={missingNumber ? t('Required') : ' '}
                    />
                    <TextField
                      inputRef={ref => { if (ref && !firstMissingRef.current && !missingNumber && missingSeal) firstMissingRef.current = ref; }}
                      label={t('Seal Number')}
                      value={c.sealNumber}
                      disabled={isLoading}
                      onChange={e => formik.setFieldValue(`containers.${idx}.sealNumber`, e.target.value.toUpperCase())}
                      size="small"
                      error={missingSeal}
                      placeholder={t('Enter seal')}
                      helperText={missingSeal ? t('Required') : ' '}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: .75 }}>
                      <Tooltip title={t('Fill then save')} placement="top" arrow>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Quick actions')}</Typography>
                      </Tooltip>
                      {highlight && (
                        <Chip size="small" color={missingSeal ? 'error' : 'warning'} variant="outlined" label={missingNumber && missingSeal ? t('Both number and seal are required') : missingNumber ? t('Number required') : t('Seal required')} sx={{ fontSize: 11 }} />
                      )}
                    </Box>
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }} />
                  </Box>
                );
              })}
              {!containers.length && (
                <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed #CBD5E1', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">{t('No containers')}</Typography>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
              </DialogContent>
              <DialogActions sx={{ p: 1.5, justifyContent: 'space-between', bgcolor: '#FFFFFF' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip size="small" label={t('Progress')} />
                  <Chip size="small" color={hasMissing ? 'warning' : 'success'} variant="outlined" label={`${filledFields}/${totalFields}`} />
                  <Chip size="small" variant="outlined" label={`${completion}%`} />
                  {updateManage.loading === 'pending' && <LinearProgress sx={{ width: 120, height: 6, borderRadius: 1 }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={onClose} variant="text" disabled={updateManage.loading === 'pending'}>{t('cancel')}</Button>
                  <Button type="submit" variant="contained" disabled={hasMissing || updateManage.loading === 'pending'}>{t('save')}</Button>
                </Box>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

// Small helper component for summary fields
function Field({ label, value, chip = false, color }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: .25 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#546E7A' }}>{label}</Typography>
      {chip ? (
        <Chip size="small" color={color || 'default'} label={value || '-'} />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
      )}
    </Box>
  );
}

function PartyCard({ title, data, dashed }) {
  if (!data) return null;
  const address = [data.street, data.city, data.country].filter(Boolean).join(', ');
  return (
    <Box sx={{ p: 1.5, border: dashed ? '1px dashed #CBD5E1' : '1px solid #E0E0E0', borderRadius: 2, bgcolor: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: .5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>{title}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.name || '-'}</Typography>
      {data.companyName && <Typography variant="caption" color="text.secondary">{data.companyName}</Typography>}
      {data.phoneNumber && <Typography variant="caption" display="block">{t('Phone')}: {data.phoneNumber}</Typography>}
      {data.email && <Typography variant="caption" display="block">{t('Email')}: {data.email}</Typography>}
      {address && <Typography variant="caption" color="text.secondary" display="block">{address}</Typography>}
    </Box>
  );
}
